/**
 * DealPaymentsTab - sub-tab on DealDetail showing all deal_payments rows for a deal.
 *
 * Replaces the previous flat pre_build_amount_received / post_build_amount_received
 * fields. RMs upload checks here at the install meeting; Shannon advances state
 * from her Finance Board. Stripe payments arrive automatically via stripe-webhook.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 3)
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { DealPayment } from "../../lib/types";
import CheckTrackerCard from "../payments/CheckTrackerCard";
import CheckUploadModal from "../payments/CheckUploadModal";
import PaymentEditModal from "../payments/PaymentEditModal";
import { fmtUSD } from "../../lib/analytics";
import { generateAndAttachInvoicePDF, type InvoiceDeal } from "../../lib/supplementInvoicePDF";

// Who can edit/delete existing payments (May 20, 2026, per Greg): super-users +
// finance/exec tier. Mirrors the deal_payments dp_write RLS policy.
function canManagePayments(role: string | undefined, isSuperUser: boolean): boolean {
  return (
    isSuperUser ||
    role === "admin" ||
    role === "finance" ||
    role === "sales_director" ||
    role === "director_of_operations" ||
    role === "roof_director"
  );
}

const COLS = "id,deal_id,slot,method,kind,iink_required,amount,state," +
  "collected_at,iink_submitted_at,mortgage_endorsed_at,fedex_out_at,fedex_back_at,iink_cleared_at,deposited_at,bank_cleared_at," +
  "stripe_payment_intent_id,stripe_charge_id,stripe_made_at,stripe_cleared_at,stripe_disputed_at,stripe_refunded_at," +
  "ach_verify_status,ach_verify_url,ach_microdeposit_type,ach_microdeposit_arrival_at," +
  "ach_verify_started_at,ach_verify_deadline_at,ach_verify_ended_at,ach_verify_checked_at," +
  "qb_payment_id,qb_synced_at,qb_sync_error,notes,created_by,created_at,updated_at";

export default function DealPaymentsTab({
  dealId,
  deal,
  reload: _reload,
  role,
  isSuperUser = false,
}: {
  dealId: string;
  deal?: InvoiceDeal;
  reload?: () => void;
  role?: string;
  isSuperUser?: boolean;
}) {
  const [payments, setPayments] = useState<DealPayment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState<DealPayment | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [pdfViewUrl, setPdfViewUrl] = useState<string | null>(null);

  const canManage = canManagePayments(role, isSuperUser);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deal_payments", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error: err } = await supabase
        .from("deal_payments")
        .select(COLS)
        .eq("deal_id", dealId)
        .order("created_at", { ascending: false });
      if (err) {
        if (!cancelled) setError(err.message);
        return;
      }
      if (!cancelled) setPayments((data ?? []) as unknown as DealPayment[]);
    }
    load();
    return () => { cancelled = true; };
  }, [dealId, reloadKey]);

  if (error) return <div className="bg-red-50 text-red-700 p-3 rounded text-sm">{"Couldn't load payments - " + error}</div>;
  if (!payments) return <div className="text-slate-500 text-sm p-3">Loading payments...</div>;

  // Group by slot for visual organization
  const groups: Record<string, DealPayment[]> = { pre_build: [], post_build: [], supplement: [], other: [] };
  for (const p of payments) (groups[p.slot] ?? groups.other).push(p);

  const totalCleared = payments
    .filter((p) => p.state === "bank_cleared" || p.state === "cleared")
    .reduce((s, p) => s + Number(p.amount), 0);
  const totalInFlight = payments
    .filter((p) => !["bank_cleared", "cleared", "voided", "refunded", "disputed"].includes(p.state))
    .reduce((s, p) => s + Number(p.amount), 0);
  // Part of "in flight" can't move until the homeowner verifies their bank
  // account (Stripe ACH typed in by hand, Oct 20, 2026).
  const awaitingVerify = payments.filter(
    (p) => p.method === "stripe" && p.state === "made" && p.ach_verify_status === "pending",
  );
  const awaitingVerifyTotal = awaitingVerify.reduce((s, p) => s + Number(p.amount), 0);

  // Determine if Generate Invoice PDF button should show
  const hasFinalInvoice = deal && Number(deal.final_invoice_amount ?? 0) > 0;
  const balanceRemaining = Number(deal?.post_build_balance_remaining ?? 0);

  return (
    <div className="p-4 space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Cleared" value={fmtUSD(totalCleared, { short: true })} sub={payments.filter(p => p.state === "bank_cleared" || p.state === "cleared").length + " payments"} valueClass="text-emerald-600" />
        <KPI label="In flight" value={fmtUSD(totalInFlight, { short: true })} sub={payments.filter(p => !["bank_cleared","cleared","voided","refunded","disputed"].includes(p.state)).length + " payments"} valueClass="text-amber-600" />
        <KPI label="Total" value={fmtUSD(totalCleared + totalInFlight, { short: true })} sub={payments.length + " payments total"} />
      </div>
      {awaitingVerify.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          🏦 {fmtUSD(awaitingVerifyTotal)} of the money in flight is waiting on the homeowner to verify their
          bank account. Nothing is charged until they do. See the payment below to text them the link.
        </div>
      )}

      {/* Action buttons row */}
      <div className="flex justify-end gap-2 flex-wrap">
        {/* Generate Invoice PDF - visible when final_invoice_amount is set */}
        {hasFinalInvoice && deal && (
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                setGeneratingPdf(true);
                setPdfError(null);
                setPdfSuccess(false);
                setPdfViewUrl(null);
                try {
                  const result = await generateAndAttachInvoicePDF(supabase, deal);
                  setPdfSuccess(true);
                  setPdfViewUrl(result.signedUrl);
                  _reload?.();
                } catch (e: any) {
                  setPdfError(e?.message ?? "PDF generation failed");
                } finally {
                  setGeneratingPdf(false);
                }
              }}
              disabled={generatingPdf || balanceRemaining <= 0}
              className="px-4 py-2 rounded text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-bardin-ink border-slate-200 hover:border-[#345c72] hover:text-[#345c72]"
              title={balanceRemaining <= 0 ? "No balance remaining - nothing to invoice" : "Generate or regenerate the invoice PDF"}
            >
              {generatingPdf ? "Generating..." : "Generate Invoice PDF"}
            </button>
            {pdfSuccess && (
              <span className="flex items-center gap-2">
                <span className="text-xs text-emerald-600 font-medium">PDF generated</span>
                {pdfViewUrl && (
                  <a
                    href={pdfViewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-[#345c72] hover:text-[#f46530] underline"
                  >
                    View PDF
                  </a>
                )}
              </span>
            )}
            {pdfError && <span className="text-xs text-red-600 font-medium">{pdfError}</span>}
          </div>
        )}
        {/* Record payment button (RM uploads check; Shannon back-dates non-check payments) */}
        <button
          onClick={() => setShowUpload(true)}
          className="px-4 py-2 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90"
        >
          Record payment
        </button>
      </div>

      {/* Slot groupings */}
      {(["pre_build", "post_build", "supplement", "other"] as const).map((slot) => {
        const list = groups[slot];
        if (list.length === 0) return null;
        return (
          <div key={slot}>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">
              {slotLabel(slot)} ({list.length})
            </div>
            <div className="space-y-2">
              {list.map((p) => (
                <CheckTrackerCard
                  key={p.id}
                  payment={p}
                  onAdvanced={reload}
                  onIinkToggled={reload}
                  canManage={canManage}
                  onEdit={() => setEditing(p)}
                  onDeleted={reload}
                  textToName={deal?.homeowner_name}
                  textToPhone={deal?.homeowner_phone}
                />
              ))}
            </div>
          </div>
        );
      })}

      {payments.length === 0 && (
        <div className="rounded border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
          <div className="text-2xl mb-2">No payments recorded yet</div>
          <div className="text-xs mt-1">Click "Record payment" to upload a check (RM, at install meeting) or back-date an ACH / wire / cash / Stripe payment (Shannon). Stripe payments via the homeowner portal appear here automatically.</div>
        </div>
      )}

      {showUpload && (
        <CheckUploadModal
          dealId={dealId}
          onClose={() => setShowUpload(false)}
          onCreated={() => { reload(); setShowUpload(false); }}
        />
      )}

      {editing && (
        <PaymentEditModal
          payment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { reload(); setEditing(null); }}
        />
      )}
    </div>
  );
}

function slotLabel(s: string): string {
  return ({
    pre_build:  "Pre-build payments",
    post_build: "Post-build / final invoice",
    supplement: "Supplement payments",
    other:      "Other",
  } as Record<string, string>)[s] ?? s;
}

function KPI({ label, value, sub, valueClass }: { label: string; value: string; sub?: string; valueClass?: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={"text-lg font-extrabold mt-1 " + (valueClass ?? "text-bardin-ink")}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
